import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Bell, LogOut, Menu } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { Sheet, SheetContent } from "../ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";

function NotificationsBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const nav = useNavigate();
  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  const open = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    load();
    if (n.link) nav(n.link);
  };
  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative rounded-md p-2 text-slate-600 hover:bg-slate-100">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-sky-600 px-1 text-center text-[10px] font-semibold leading-[18px] text-white">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <span className="text-sm font-semibold">Notifications</span>
          <button data-testid="notifications-read-all" className="text-xs text-sky-700 hover:underline" onClick={() => api.post("/notifications/read-all").then(load)}>
            Mark all read
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <div className="p-6 text-center text-xs text-slate-400">No notifications</div>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid={`notification-item-${n.id}`}
              className={`block w-full border-b border-slate-100 px-4 py-2.5 text-left hover:bg-slate-50 ${n.is_read ? "" : "bg-sky-50/60"}`}>
              <div className="text-xs font-medium text-slate-800">{n.title}</div>
              {n.body && <div className="mt-0.5 line-clamp-2 text-[11px] text-slate-500">{n.body}</div>}
              <div className="mt-1 text-[10px] text-slate-400">{fmtDateTime(n.created_at)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Topbar({ onMenu }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur md:px-8">
      <button className="rounded-md p-2 text-slate-600 hover:bg-slate-100 lg:hidden" onClick={onMenu} data-testid="mobile-menu-button">
        <Menu className="h-5 w-5" />
      </button>
      <div className="min-w-0 flex-1">
        <div data-testid="current-lgu-name" className="truncate text-sm font-semibold text-slate-800">
          {user.lgu ? user.lgu.name : "Platform Administration"}
          {user.lgu?.is_demo && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">DEMO TENANT</span>}
        </div>
        <div className="truncate text-[11px] text-slate-500">{user.lgu?.legislative_body_name || "No legislative records access"}</div>
      </div>
      {user.lgu && <NotificationsBell />}
      <div className="hidden text-right md:block">
        <div className="text-xs font-medium text-slate-800" data-testid="current-user-name">{user.full_name}</div>
        <div className="max-w-[240px] truncate text-[10px] text-slate-500">{user.roles.map((r) => r.name).join(", ")}</div>
      </div>
      <Button variant="ghost" size="sm" data-testid="logout-button" onClick={async () => { await logout(); nav("/login"); }}>
        <LogOut className="h-4 w-4" />
      </Button>
    </header>
  );
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex min-h-screen">
      <aside className="no-print fixed inset-y-0 left-0 hidden w-64 lg:block">
        <Sidebar />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 border-0 p-0">
          <Sidebar onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <div className="no-print">
          <Topbar onMenu={() => setOpen(true)} />
        </div>
        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
